import type { UpdateEntityMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateEntityTRPCInput = {
  Id: string;
  Name: string;
  Description?: string | null;
  ParentId?: string | null;
  Weight: number;
  OwnerUserIds?: string[] | null;
  OwnerGroupIds?: string[] | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure.
 * The tRPC update returns `{ Id }`; the GraphQL shape wants `affected_rows`,
 * so we map to 1 on success.
 */
const mapTrpcResponseToGraphQL = (): UpdateEntityMutation => {
  return {
    updateChildEntity: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  };
};

export const useUpdateEntityTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.entity.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.entity.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.entity.getById.queryKey(),
        });
      },
    }),
  });

  return {
    updateEntity: async (
      variables: UpdateEntityTRPCInput
    ): Promise<UpdateEntityMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
